/** Image uploader (drag-drop) — переиспользуем в pages_cms, site-content,
 * brands и других местах. POST'ит в /pages/media/upload, возвращает URL.
 */

import { Loader2, Upload, X } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export function ImageUploader({
  value,
  onChange,
  onRemove,
  className,
  previewClassName,
  hint = "Загрузить картинку",
}: {
  value?: string;
  onChange: (url: string) => void;
  onRemove?: () => void;
  className?: string;
  previewClassName?: string;
  hint?: string;
}) {
  const [busy, setBusy] = useState(false);

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`${file.name}: > 10 MB`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{ url: string }>(
        "/pages/media/upload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onChange(data.url);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? "Не удалось загрузить");
    } finally {
      setBusy(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // .svg исключён: бэкенд re-encode'ит всё через Pillow (Image.open().verify())
    // — SVG он не парсит, upload всегда падает 422. Раньше accept предлагал его
    // выбрать, что просто вело к гарантированной ошибке при отправке.
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    maxFiles: 1,
    multiple: false,
  });

  if (value) {
    return (
      <div
        className={cn(
          "relative group border rounded overflow-hidden",
          className,
        )}
      >
        <img
          src={value}
          alt=""
          className={cn(
            "w-full h-32 object-contain bg-muted/30 p-2",
            previewClassName,
          )}
        />
        <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">
          {value}
        </div>
        {onRemove && (
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition"
            onClick={onRemove}
            type="button"
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        "border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors text-sm",
        isDragActive
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/30 hover:border-muted-foreground/50",
        className,
      )}
    >
      <input {...getInputProps()} />
      {busy ? (
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Загрузка…
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
          <Upload className="w-4 h-4" />
          {isDragActive ? "Отпусти файл" : hint}
        </div>
      )}
    </div>
  );
}
